import { useState } from 'react';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import TreeView from '../shared/TreeView';
import {
  buildUnitCodesFromMasterData,
  useScopeClearAll
} from '../shared/scopeUtils';

export default function ScopeQuickFilter({
  masterInputTreeJson,
  selectedUnits,
  onApply
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [clearAll, setClearAll] = useScopeClearAll(selectedUnits);
  const [pendingUnits, setPendingUnits] = useState([]);

  const isOpen = Boolean(anchorEl);
  const isSelected = !!(selectedUnits && selectedUnits.length > 0);
  const hasPendingSelection = isOpen && pendingUnits.length > 0;

  const handleMasterDataChange = (nextData, isDirty) => {
    if (isDirty && nextData && nextData.length > 0) {
      setPendingUnits(buildUnitCodesFromMasterData(nextData));
    }
  };

  const handleOpen = (event) => {
    if (isOpen) {
      handleClose();
      return;
    }
    setAnchorEl(event.currentTarget);
    setClearAll(false);
    setPendingUnits(selectedUnits ?? []); // seed from committed selection so an immediate Apply/close is safe
  };

  const handleClose = () => {
    if (hasSelectionChanged(pendingUnits, selectedUnits)) {
      onApply(pendingUnits);
    }
    setAnchorEl(null);
  };

  const isActive = isSelected || hasPendingSelection;

  function hasSelectionChanged(pending, committed) {
    const a = [...pending].sort();
    const b = [...(committed ?? [])].sort();
    return a.length !== b.length || a.some((v, i) => v !== b[i]);
  }

  return (
    <>
      <Chip
        variant="outlined"
        aria-pressed={isActive}
        sx={(theme) =>
          isActive
            ? {
                fontWeight: 'normal',
                backgroundColor: theme.fuel6Tokens.JddcColorBackgroundBrand,
                color: theme.fuel6Tokens.JddcColorTextInverse,
                borderColor: theme.fuel6Tokens.JddcColorBorderBrand,
                '&&:hover': {
                  backgroundColor: theme.fuel6Tokens.JddcColorBackgroundBrand,
                  borderColor: theme.fuel6Tokens.JddcColorBorderBrand
                },
                '&:focus': {
                  backgroundColor: theme.fuel6Tokens.JddcColorBackgroundBrand
                }
              }
            : {
                fontWeight: 'normal',
                '&&': {
                  border: `1px solid ${theme.fuel6Tokens.JddcColorBorderInteractive}`
                }
              }
        }
        label={
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            Scope
            <fuel-icon name="keyboard_arrow_down" style={{ fontSize: '1rem', verticalAlign: 'middle' }} />
          </span>
        }
        onClick={handleOpen}
        data-testid="scope-chip"
      />

      <Popover
        id="scope-quick-filter-popover"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            style: {
              width: '340px',
              borderRadius: '8px',
              marginTop: '4px',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
            }
          }
        }}
      >
        <div style={{ padding: '12px 8px' }}>
          <TreeView
            masterInputTreeJson={masterInputTreeJson}
            showInOneColumn={true}
            showFilter={true}
            filterPlaceHolder="Filter by..."
            donotShowClearButton={true}
            addVScrollBar={true}
            setParentMasterData={handleMasterDataChange}
            setParentClearAllFlag={setClearAll}
            clearAllFlag={clearAll}
            selectedUnits={selectedUnits ?? []}
            invalidError={null}
            compactMode={true}
          />
        </div>
      </Popover>
    </>
  );
}
